import b4a from 'b4a'
import z32 from 'z32'
import {
  domainHash, sign, seal, sealOpen, encrypt, decrypt, randomBytes, toBoxPublicKey
} from '@pine/crypto'
import { ROOM_TYPE } from '@pine/protocol'

/**
 * Stories, from the app's side: a photo or a video for a day, to everyone
 * this account has a one-to-one chat with.
 *
 * The server keeps them (see server/relay/lib/stories.js for exactly what it
 * holds): a story has to be there when its author's phone is not. What it
 * keeps is ciphertext. Each story gets a key of its own; the key goes to the
 * server sealed once per person who may see the story, to that person's own
 * account key — only they can open their copy, and the server cannot open
 * any. The caption travels inside the ciphertext with the picture.
 *
 * The audience is decided when the story is posted: somebody who starts a
 * chat with the author an hour later does not see it.
 */

export const STORY_MAX_BYTES = 25 * 1024 * 1024
// How often a running app asks for the feed on its own. The chat list also
// asks whenever it is looked at again (see pine-ui screens/stories.js).
export const STORY_REFRESH = 2 * 60 * 1000
const TIMEOUT = 15000

/* ---- what both ends sign; byte-identical to server/relay/lib/stories.js ---- */

export function storyKeysDigest (keys) {
  const pairs = (Array.isArray(keys) ? keys : [])
    .map((k) => String(k.to) + ':' + String(k.key))
    .sort()
  return b4a.toString(domainHash('pine/stories/keys/v1', pairs.join(',')), 'hex')
}

export function storyPublishSignable ({ account, at, id, size, hash, keys }) {
  return domainHash('pine/stories/publish/v1', String(account), String(at), String(id),
    String(size), String(hash), storyKeysDigest(keys))
}

export function storyActionSignable ({ action, account, at, id = '' }) {
  return domainHash('pine/stories/' + action + '/v1', String(account), String(at), String(id || ''))
}

export function storyBlobHash (blob) {
  return b4a.toString(domainHash('pine/stories/blob/v1', blob), 'hex')
}

/* ---- the plaintext inside a story's ciphertext ---- */

/** A small JSON header (type, caption, size, duration), then the media bytes. */
export function packStory ({ data, mime, caption = '', width = 0, height = 0, duration = 0 }) {
  const header = b4a.from(JSON.stringify({
    v: 1, mime: String(mime || ''), caption: String(caption || '').slice(0, 1024),
    w: width || 0, h: height || 0, d: duration || 0
  }), 'utf8')
  const length = b4a.allocUnsafe(4)
  b4a.writeUInt32LE(length, header.byteLength, 0)
  return b4a.concat([length, header, b4a.from(data)])
}

export function unpackStory (plain) {
  if (!plain || plain.byteLength < 4) return null
  const length = b4a.readUInt32LE(plain, 0)
  if (4 + length > plain.byteLength) return null
  let header
  try {
    header = JSON.parse(b4a.toString(plain.subarray(4, 4 + length), 'utf8'))
  } catch {
    return null
  }
  return {
    mime: String(header.mime || ''),
    caption: String(header.caption || ''),
    width: header.w || 0,
    height: header.h || 0,
    duration: header.d || 0,
    bytes: plain.subarray(4 + length)
  }
}

export class Stories {
  constructor (client) {
    this.client = client
    this.items = []          // the last feed: [{ id, author, at, expires, size, key, seen, views, mine }]
    this.fetchedAt = 0
    this._timer = null
    this._refreshing = null
    this._seen = new Set()   // ids this device has opened, so a ring greys at once
  }

  get url () {
    return (this.client.registryUrl || '').replace(/\/+$/, '')
  }

  get enabled () {
    return !!this.url
  }

  start () {
    if (this._timer || !this.enabled) return
    this._timer = setInterval(() => this.refresh().catch(() => {}), STORY_REFRESH)
    if (this._timer.unref) this._timer.unref()
    setTimeout(() => this.refresh().catch(() => {}), 5000)
  }

  stop () {
    if (this._timer) clearInterval(this._timer)
    this._timer = null
  }

  /** Everyone this account has a one-to-one chat with — not bots — and itself. */
  async audience () {
    const out = new Set()
    for (const record of this.client.summaries.values()) {
      if (record.deleted || record.placeholder || record.type !== ROOM_TYPE.DM || !record.peer) continue
      if (record.assistant) continue
      out.add(record.peer)
    }
    for (const account of [...out]) {
      const contact = await this.client.getContact(account).catch(() => null)
      if (contact && (contact.bot || contact.blocked)) out.delete(account)
    }
    // A copy for this account too: its other devices see the story, and the
    // count of who has watched it.
    out.add(this.client.identity.id)
    return [...out]
  }

  /**
   * Post a story.
   * @param data   the photo or video bytes (already resized by the caller)
   * @returns the story as it now appears in the feed
   */
  async publish ({ data, mime, caption = '', width = 0, height = 0, duration = 0, onProgress = null }) {
    if (!this.enabled) throw new Error('Stories need the Pine server')
    const key = randomBytes(32)
    const blob = encrypt(packStory({ data, mime, caption, width, height, duration }), key)
    if (blob.byteLength > STORY_MAX_BYTES) {
      const error = new Error('That file is too big for a story')
      error.reason = 'too_large'
      throw error
    }

    const recipients = await this.audience()
    const keys = recipients.map((account) => ({
      to: account,
      key: b4a.toString(seal(key, toBoxPublicKey(z32.decode(account))), 'base64')
    }))

    const id = b4a.toString(randomBytes(16), 'hex')
    const hash = storyBlobHash(blob)
    const at = Date.now()
    const account = this.client.identity.id
    const signature = sign(storyPublishSignable({ account, at, id, size: blob.byteLength, hash, keys }), this.client.identity.secretKey)

    await this._json('/api/stories/publish', {
      account, at, id, size: blob.byteLength, hash, keys, signature: b4a.toString(signature, 'hex')
    })
    if (onProgress) onProgress(0.1)
    await this._upload(id, blob)
    if (onProgress) onProgress(1)

    await this.refresh()
    return this.items.find((s) => s.id === id) || { id, author: account, at, mine: true, key }
  }

  /** The stories this account may see now, its own included. */
  async refresh () {
    if (!this.enabled || !this.client.onboarded) return this.items
    if (this._refreshing) return this._refreshing
    this._refreshing = (async () => {
      try {
        const body = await this._signedAction('feed')
        const me = this.client.identity.id
        const boxPublic = this.client.identity.boxPublicKey
        const boxSecret = this.client.identity.boxSecretKey
        this.items = (body.stories || []).map((story) => {
          let key = null
          if (story.key) {
            const opened = sealOpen(b4a.from(story.key, 'base64'), boxPublic, boxSecret)
            if (opened && opened.byteLength === 32) key = opened
          }
          return {
            id: story.id,
            author: story.author,
            at: story.at,
            expires: story.expires,
            size: story.size,
            key,
            seen: !!story.seen || this._seen.has(story.id),
            views: story.views,
            mine: story.author === me
          }
        }).filter((story) => story.key) // one we cannot open is one we cannot show
        this.fetchedAt = Date.now()
        this.client.emit('stories', this.items)
        return this.items
      } finally {
        this._refreshing = null
      }
    })()
    return this._refreshing
  }

  /** Grouped for the bar at the top of the chat list: mine first, then unseen first. */
  byAuthor () {
    const groups = new Map()
    for (const story of this.items) {
      if (story.expires && story.expires <= Date.now()) continue
      if (this.client.isBlocked && this.client.isBlocked(story.author)) continue
      if (!groups.has(story.author)) groups.set(story.author, [])
      groups.get(story.author).push(story)
    }
    const me = this.client.identity.id
    return [...groups.entries()]
      .map(([author, list]) => ({
        author,
        mine: author === me,
        stories: list.sort((a, b) => a.at - b.at),
        unseen: list.some((s) => !s.seen && author !== me),
        latest: Math.max(...list.map((s) => s.at))
      }))
      .sort((a, b) => (b.mine - a.mine) || (b.unseen - a.unseen) || (b.latest - a.latest))
  }

  /** Download and decrypt one: { mime, caption, width, height, duration, bytes }. */
  async open (story) {
    if (!story || !story.key) throw new Error('No key for this story')
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 60000)
    let bytes
    try {
      const response = await fetch(this.url + '/api/stories/blob/' + story.id, { signal: controller.signal })
      if (!response.ok) {
        const error = new Error('This story is gone')
        error.reason = 'gone'
        throw error
      }
      bytes = new Uint8Array(await response.arrayBuffer())
    } finally {
      clearTimeout(timer)
    }
    const plain = decrypt(bytes, story.key)
    const unpacked = plain && unpackStory(plain)
    if (!unpacked) throw new Error('This story could not be decrypted')
    return unpacked
  }

  /** Watched: the ring greys here at once, and the author's count goes up. */
  async markSeen (story) {
    if (!story || story.mine || this._seen.has(story.id)) return
    this._seen.add(story.id)
    story.seen = true
    this.client.emit('stories', this.items)
    await this._signedAction('view', story.id).catch(() => {})
  }

  /** Who has watched one of my stories: [{ account, at }]. */
  async viewers (story) {
    const body = await this._signedAction('views', story.id)
    return body.viewers || []
  }

  async remove (story) {
    await this._signedAction('delete', story.id)
    this.items = this.items.filter((s) => s.id !== story.id)
    this.client.emit('stories', this.items)
  }

  /* ---- wire ---- */

  async _signedAction (action, id = '') {
    const at = Date.now()
    const account = this.client.identity.id
    const signature = sign(storyActionSignable({ action, account, at, id }), this.client.identity.secretKey)
    return this._json('/api/stories/' + action, { account, at, id, signature: b4a.toString(signature, 'hex') })
  }

  async _json (path, body) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), TIMEOUT)
    try {
      const response = await fetch(this.url + path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal
      })
      const answer = await response.json().catch(() => ({}))
      if (!response.ok) {
        const error = new Error(answer.error || 'HTTP ' + response.status)
        error.reason = answer.error || 'failed'
        throw error
      }
      return answer
    } finally {
      clearTimeout(timer)
    }
  }

  async _upload (id, blob) {
    const controller = new AbortController()
    // A video over mobile data takes a while; a minute per 5 MB is generous.
    const timer = setTimeout(() => controller.abort(), Math.max(60000, (blob.byteLength / (5 * 1024 * 1024)) * 60000))
    try {
      const response = await fetch(this.url + '/api/stories/blob/' + id, {
        method: 'POST',
        headers: { 'content-type': 'application/octet-stream' },
        body: blob,
        signal: controller.signal
      })
      if (!response.ok) {
        const answer = await response.json().catch(() => ({}))
        const error = new Error(answer.error || 'Upload failed')
        error.reason = answer.error || 'failed'
        throw error
      }
    } finally {
      clearTimeout(timer)
    }
  }
}
